// ============================================================
// Importer — JSON-Datei lesen, validieren, normalisieren
// ============================================================

import type { Eintrag, ErgebnisTyp, Spielart, Spielhistorie, SpielStatus } from './types';

export interface ValidationIssue {
  /** Pfad zum fehlerhaften Eintrag (z. B. "Eintrag 27, ID 379494118") */
  loc: string;
  /** Beschreibung des Problems */
  msg: string;
}

export interface ValidationResult {
  ok: boolean;
  issues: ValidationIssue[];
  data?: Spielhistorie;
}

const REQUIRED_FIELDS = ['id', 'abgabeIso', 'spielart', 'einsatzEuro', 'gewinnEuro', 'status', 'ergebnis'] as const;

const VALID_STATUS: SpielStatus[] = ['teilnehmend', 'in Auswertung', 'abgeschlossen', 'archiviert'];

/**
 * Parst rohe JSON-Daten, prüft Struktur und Pflichtfelder,
 * normalisiert die Einträge. Bei Fehlern liefert ok=false mit
 * konkreten Issues; ok=true => data ist garantiert gültig.
 */
export function validateAndNormalize(raw: unknown): ValidationResult {
  const issues: ValidationIssue[] = [];

  if (raw === null || typeof raw !== 'object') {
    issues.push({ loc: 'Datei', msg: 'Root muss ein Objekt sein.' });
    return { ok: false, issues };
  }

  const obj = raw as Record<string, unknown>;

  if (!('eintraege' in obj)) {
    issues.push({ loc: 'Root', msg: 'Pflichtfeld "eintraege" fehlt.' });
    return { ok: false, issues };
  }
  if (!Array.isArray(obj.eintraege)) {
    issues.push({ loc: 'Root', msg: '"eintraege" muss ein Array sein.' });
    return { ok: false, issues };
  }

  const arr = obj.eintraege as unknown[];
  const seenIds = new Map<string, number>();
  const eintraege: Eintrag[] = [];
  const zusammenfassungRaw = (obj.zusammenfassung ?? {}) as Record<string, unknown>;

  arr.forEach((entry, idx) => {
    const humanIdx = idx + 1;
    if (entry === null || typeof entry !== 'object') {
      issues.push({ loc: `Eintrag ${humanIdx}`, msg: 'Eintrag ist kein Objekt.' });
      return;
    }
    const e = entry as Record<string, unknown>;
    const id = typeof e.id === 'string' ? e.id : '';
    const idLabel = id ? `Eintrag ${humanIdx}, ID ${id}` : `Eintrag ${humanIdx}`;

    // Pflichtfelder
    for (const f of REQUIRED_FIELDS) {
      if (!(f in e)) {
        issues.push({ loc: idLabel, msg: `Pflichtfeld "${f}" fehlt.` });
      }
    }

    // ID-Duplikate
    if (id) {
      if (seenIds.has(id)) {
        issues.push({
          loc: idLabel,
          msg: `ID ist nicht eindeutig (Duplikat von Eintrag ${seenIds.get(id)}).`,
        });
      } else {
        seenIds.set(id, humanIdx);
      }
    }

    // abgabeIso
    if (typeof e.abgabeIso === 'string') {
      const t = Date.parse(e.abgabeIso);
      if (isNaN(t)) {
        issues.push({ loc: idLabel, msg: 'Feld "abgabeIso" ist kein gültiges Datum.' });
      }
    }

    // spielart
    if (typeof e.spielart !== 'string' || !e.spielart.trim()) {
      issues.push({ loc: idLabel, msg: 'Pflichtfeld "spielart" fehlt oder leer.' });
    }

    // einsatzEuro / gewinnEuro
    const einsatz = typeof e.einsatzEuro === 'number' ? e.einsatzEuro : NaN;
    if (!isFinite(einsatz) || einsatz < 0) {
      issues.push({ loc: idLabel, msg: 'Feld "einsatzEuro" enthält keinen gültigen, nicht-negativen Zahlenwert.' });
    }
    const gewinn = typeof e.gewinnEuro === 'number' ? e.gewinnEuro : NaN;
    if (!isFinite(gewinn) || gewinn < 0) {
      issues.push({ loc: idLabel, msg: 'Feld "gewinnEuro" enthält keinen gültigen, nicht-negativen Zahlenwert.' });
    }

    // status
    if (!VALID_STATUS.includes(e.status as SpielStatus)) {
      issues.push({
        loc: idLabel,
        msg: `Feld "status" muss einer von ${VALID_STATUS.join(', ')} sein.`,
      });
    }

    // ergebnis.typ
    const ergebnis = e.ergebnis as { typ?: string } | undefined;
    if (!ergebnis || (ergebnis.typ !== 'gewinn' && ergebnis.typ !== 'verlust')) {
      issues.push({ loc: idLabel, msg: 'Feld "ergebnis.typ" muss "gewinn" oder "verlust" sein.' });
    }

    if (issues.some((i) => i.loc === idLabel)) return;

    // Felder vorhanden & gültig → normalisieren
    eintraege.push({
      id,
      iso: e.abgabeIso as string,
      spielart: e.spielart as Spielart,
      einsatz,
      gewinn,
      rohtext: (ergebnis as { rohtext?: string })?.rohtext ?? '',
      typ: ergebnis!.typ as ErgebnisTyp,
      status: e.status as SpielStatus,
      detailUrl: typeof e.detailUrl === 'string' ? e.detailUrl : '',
    });
  });

  if (issues.length > 0) {
    return { ok: false, issues };
  }

  const zusammenfassung = {
    einsatzGesamt: Number(zusammenfassungRaw.einsatzGesamt) || 0,
    gewinnGesamt: Number(zusammenfassungRaw.gewinnGesamt) || 0,
    saldo: Number(zusammenfassungRaw.saldo) || 0,
  };

  return {
    ok: true,
    issues: [],
    data: {
      exportiertAm: typeof obj.exportiertAm === 'string' ? obj.exportiertAm : undefined,
      quelle: typeof obj.quelle === 'string' ? obj.quelle : undefined,
      seitenGesamt: typeof obj.seitenGesamt === 'number' ? obj.seitenGesamt : undefined,
      anzahlEintraege: eintraege.length,
      zusammenfassung,
      eintraege,
    },
  };
}

/** Liest eine Datei aus <input type="file"> oder einem Drag-Drop-Event. */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}
